import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MapLayerMouseEvent, useMap } from 'react-map-gl';
import { ImageCoords } from '@features/map-layers-list/new-layer-file-uploader/model/types';
import {
    handlesGeoJSON,
    rotateCoords,
} from '@features/map-layers-list/new-layer-file-uploader/model/utils';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectLayerClickabilityById } from '@redux/selectors/selectors';
import { MAP_COMMON_SOURCES } from '@utils/constants';

type Props = {
    children: React.ReactNode;
};

type ImageEditorContextValue = {
    coords: ImageCoords;
    setCoords: React.Dispatch<React.SetStateAction<ImageCoords>>;
    rotate: (angleRad: number) => void;
    handlesGeoJSON: GeoJSON.FeatureCollection;
    mapHandlers: {
        onMouseDown: (e: MapLayerMouseEvent) => void;
        onMouseMove: (e: MapLayerMouseEvent) => void;
        onMouseUp: () => void;
    };
};

const ImageLayerContext = createContext<ImageEditorContextValue | null>(null);

export function useImageLayer() {
    const ctx = useContext(ImageLayerContext);
    if (!ctx) {
        throw new Error('useImageEditor must be used inside ImageEditorProvider');
    }
    return ctx;
}

export function ImageLayerProvider({ children }: Props) {
    const [coords, setCoords] = useState<ImageCoords>([] as any);
    const resizeLayerClickable = useTypedSelector(state =>
        selectLayerClickabilityById(state, MAP_COMMON_SOURCES.resizeLayer)
    );

    const { mapboxMapId } = useMap();

    const draggingRef = useRef(false);
    const activeHandleRef = useRef<number | null>(null);

    useEffect(() => {
        const map = mapboxMapId?.getMap();
        if (!map) {
            return;
        }

        if (resizeLayerClickable) {
            map.dragPan.disable();
            // map.scrollZoom.disable();
            map.doubleClickZoom.disable();
        } else {
            map.dragPan.enable();
            // map.scrollZoom.enable();
            map.doubleClickZoom.enable();
        }
    }, [resizeLayerClickable, mapboxMapId]);

    const onMouseDown = useCallback(
        (e: MapLayerMouseEvent) => {
            if (!resizeLayerClickable) {
                return;
            }

            const feature = e.features?.[0];
            if (!feature) {
                return;
            }

            draggingRef.current = true;
            activeHandleRef.current = feature.properties?.index ?? null;
        },
        [resizeLayerClickable]
    );

    const onMouseMove = useCallback((e: MapLayerMouseEvent) => {
        if (!draggingRef.current) {
            return;
        }
        if (activeHandleRef.current === null) {
            return;
        }

        const { lng, lat } = e.lngLat;

        setCoords(prev => {
            const next = [...prev] as ImageCoords;
            next[activeHandleRef.current!] = [lng, lat];
            return next;
        });
    }, []);

    const onMouseUp = useCallback(() => {
        draggingRef.current = false;
        activeHandleRef.current = null;
    }, []);

    const rotate = useCallback((angleRad: number) => {
        setCoords(prev => rotateCoords(prev, angleRad));
    }, []);

    const value: ImageEditorContextValue = {
        coords,
        setCoords,
        rotate,
        handlesGeoJSON: handlesGeoJSON(coords),
        mapHandlers: {
            onMouseDown,
            onMouseMove,
            onMouseUp,
        },
    };

    return <ImageLayerContext.Provider value={value}>{children}</ImageLayerContext.Provider>;
}
